/* eslint-disable no-nested-ternary */

"use client"

import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"
import Image from "next/image"
import Link from "next/link"
import React, { useCallback, useEffect, useRef, useState } from "react"

import type { ContentfulBuiltInFields } from "@/types/contentful"
import type { SEO } from "@/types/seo"

import ArrowCarouselDark from "../../../public/icons/arrow-carousel-dark.svg"
import ArrowCarousel from "../../../public/icons/arrow-carousel.svg"

type TLatestBlogPost = ContentfulBuiltInFields & {
  title: string
  date: string
  slug: string
  seo: SEO
}

export type TLatestBlogPosts = TLatestBlogPost[]

interface BPProps extends React.ComponentProps<"section"> {
  title?: string
  darkMode?: boolean
  data: TLatestBlogPosts
}

const LatestBlogPostsCarousel: React.FC<BPProps> = ({
  data,
  title,
  darkMode = true,
  className,
}) => {
  const divRef = useRef<HTMLInputElement>(null)
  const [leftMargin, setLeftMargin] = React.useState(32)

  // Embla carousel setup
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 1,
    align: "start",
    containScroll: "trimSnaps",
    dragFree: false,
  })

  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((api: any) => {
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  useEffect(() => {
    function handleResize() {
      // This function is used to calculate the offset needed from the Carousel section
      // We set an id to the wrapper which is using the PageGrid grid
      // We then set the margin-left to the inner carousel wrapper
      // This auto updates on load and resize
      const offset = divRef.current?.offsetLeft
      // eslint-disable-next-line no-void, no-unused-expressions
      offset !== undefined && void setLeftMargin(offset)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  })

  const Arrow = darkMode ? ArrowCarousel : ArrowCarouselDark

  return (
    <section
      className={clsx(
        "py-10 md:py-24",
        darkMode ? "bg-gray-90" : "bg-gray-2.5",
        className,
      )}
    >
      <div
        className="mx-4 lg:max-w-layout-lg lg:mx-auto xl:max-w-layout-xl"
        ref={divRef}
      >
        {title && (
          <h3
            className={clsx(
              "p-4 mb-6 md:mb-24 heading1 md:headline5 lg:headline4 ",
              darkMode ? "text-gray-5" : "text-gray-90",
            )}
          >
            {title}
          </h3>
        )}
      </div>
      <div className="pl-4 pr-8 md:pr-0" style={{ marginLeft: leftMargin }}>
        {/* Embla Carousel */}
        <div className="embla" ref={emblaRef}>
          <div className="embla__container">
            {data?.map(({ date, title: blogTitle, seo, slug }) => {
              const formattedDate = new Date(date).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })
              const featuredImage = seo?.featuredImage?.url

              return (
                <div
                  key={blogTitle}
                  className="embla__slide flex-[0_0_85%] min-w-0 pr-4 md:pr-8 md:flex-[0_0_42%] lg:flex-[0_0_33%] xl:flex-[0_0_28%] min-h-[450px] flex items-stretch"
                >
                  <div className="w-full">
                    <Link href={`/insights/${slug}`}>
                      <div className="relative max-w-[528px] max-h-[297px] aspect-[528/297] overflow-hidden rounded-lg mb-5 md:mb-6 xl:mb-10">
                        <Image
                          className="object-cover w-full h-full rounded-lg "
                          src={featuredImage}
                          alt="Featured Blog Post"
                          fill
                          sizes="(min-width: 528px) 528px, 100vw"
                        />
                      </div>
                      <article className="py-2">
                        <p
                          className={clsx(
                            "heading2 mb-6",
                            darkMode ? "text-gray-2.5" : "text-gray-90",
                          )}
                        >
                          {blogTitle}
                        </p>
                        <div className="flex items-center">
                          <span
                            className={clsx(
                              "caption1",
                              darkMode ? "text-gray-15" : "text-gray-50",
                            )}
                          >
                            {formattedDate}
                          </span>
                        </div>
                      </article>
                    </Link>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Navigation buttons */}
        <div className="mt-10 md:mt-4">
          <button
            type="button"
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className="disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50"
          >
            <Arrow className="mr-10 transform -rotate-180 h-14 w-14" />
          </button>
          <button
            type="button"
            onClick={scrollNext}
            disabled={!canScrollNext}
            className="disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50"
          >
            <Arrow className="h-14 w-14" />
          </button>
        </div>
      </div>
    </section>
  )
}

export default LatestBlogPostsCarousel
